interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiry: number;
  accessCount: number;
  lastAccessed: number;
}

// LRU Cache configuration
const CACHE_SIZE_LIMIT = 50; // Maximum number of items in cache
const CACHE_KEYS = new Set<string>(); // Track all cache keys for LRU eviction

function getCachedData<T>(key: string): T | null {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const cachedItem: CacheItem<T> = JSON.parse(item);
    if (Date.now() > cachedItem.expiry) {
      localStorage.removeItem(key);
      CACHE_KEYS.delete(key);
      return null;
    }

    // Update access statistics for LRU
    cachedItem.accessCount = (cachedItem.accessCount || 0) + 1;
    cachedItem.lastAccessed = Date.now();
    localStorage.setItem(key, JSON.stringify(cachedItem));

    return cachedItem.data;
  } catch (error) {
    console.error("Error reading from cache:", error);
    return null;
  }
}

function setCachedData<T>(key: string, data: T, duration: number): void {
  try {
    // Check if we need to evict items due to cache size limit
    if (CACHE_KEYS.size >= CACHE_SIZE_LIMIT) {
      evictLRUItems();
    }

    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + duration,
      accessCount: 0,
      lastAccessed: Date.now(),
    };

    localStorage.setItem(key, JSON.stringify(item));
    CACHE_KEYS.add(key);
  } catch (error) {
    console.error("Error writing to cache:", error);
  }
}

// LRU eviction function
function evictLRUItems(): void {
  try {
    const items: Array<{
      key: string;
      lastAccessed: number;
      accessCount: number;
    }> = [];

    // Collect all cache items with their access statistics
    for (const key of CACHE_KEYS) {
      const item = localStorage.getItem(key);
      if (item) {
        try {
          const cachedItem: CacheItem<any> = JSON.parse(item);
          items.push({
            key,
            lastAccessed: cachedItem.lastAccessed || cachedItem.timestamp,
            accessCount: cachedItem.accessCount || 0,
          });
        } catch (e) {
          // Remove corrupted items
          localStorage.removeItem(key);
          CACHE_KEYS.delete(key);
        }
      }
    }

    // Sort by access count (ascending) and then by last accessed time (ascending)
    items.sort((a, b) => {
      if (a.accessCount !== b.accessCount) {
        return a.accessCount - b.accessCount;
      }
      return a.lastAccessed - b.lastAccessed;
    });

    // Remove the least recently used items (20% of cache)
    const itemsToRemove = Math.ceil(CACHE_SIZE_LIMIT * 0.2);
    for (let i = 0; i < itemsToRemove && i < items.length; i++) {
      const key = items[i].key;
      localStorage.removeItem(key);
      CACHE_KEYS.delete(key);
      console.log(`🗑️ Evicted cache item: ${key}`);
    }
  } catch (error) {
    console.error("Error during LRU eviction:", error);
  }
}

export { getCachedData, setCachedData };
